import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Receipt,
  Inventory,
  Add,
  People,
  EmojiEvents,
  AttachMoney,
  BarChart,
  Block,
  Hotel
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { BudgetApiService } from '../../services/budgetApiService';
import { AdvancedDashboardStats, UserActivityStats, UserSalesStats } from '../../types';
import { ProductStatsData } from '../../types';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const handleCreateBudget = () => {
    navigate('/budgets/new');
  };
  
  const [stats, setStats] = useState<AdvancedDashboardStats>({
    total_budgets: 0,
    budgets_this_month: 0,
    total_active_products: 0,
    monthly_revenue: 0,
    budgets_by_status: {
      draft: 0,
      closed: 0,
      sold: 0
    },
    conversion_rate_to_approved: 0,
    conversion_rate_to_sold: 0,
    average_budget_value: 0,
    pending_budgets: 0,
    total_sold_revenue: 0,
    total_approved_revenue: 0,
    total_sold_revenue_usd: 0,
    total_sold_revenue_ars: 0,
    total_sold_materials_usd: 0,
    total_sold_materials_ars: 0,
    total_sold_flete_ars: 0,
    total_sold_installation_ars: 0
  });
  const [userActivityStats, setUserActivityStats] = useState<UserActivityStats | null>(null);
  const [userSalesStats, setUserSalesStats] = useState<UserSalesStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Estadísticas de productos (solo admin)
  const [mostUsedProducts, setMostUsedProducts] = useState<ProductStatsData[]>([]);
  const [productsWithoutStock, setProductsWithoutStock] = useState<ProductStatsData[]>([]);
  const [outletProducts, setOutletProducts] = useState<ProductStatsData[]>([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Usar el nuevo endpoint de estadísticas avanzadas
        const statsResponse = await BudgetApiService.getAdvancedStats();
        setStats(statsResponse);
        // Cargar estadísticas de productos para todos (admin y usuarios normales)
        try {
          const [mostUsed, productsWithoutStock, outletProducts] = await Promise.all([
            BudgetApiService.getProductsMostUsed(1000),
            BudgetApiService.getProductsWithoutStock(1000),
            BudgetApiService.getProductsOutlet(1000)
          ]);
          setMostUsedProducts(mostUsed);
          setProductsWithoutStock(productsWithoutStock);
          setOutletProducts(outletProducts);
        } catch (productStatsError) {
          console.warn('Error loading product stats:', productStatsError);
        }
        // Solo cargar estadísticas de usuarios si es admin
        if (user?.role === 'admin') {
          try {
            const [activityStats, salesStats] = await Promise.all([
              BudgetApiService.getUserActivityStats(5),
              BudgetApiService.getUserSalesStats(5)
            ]);
            setUserActivityStats(activityStats);
            setUserSalesStats(salesStats);
          } catch (userStatsError) {
            console.warn('Error loading user stats:', userStatsError);
          }
        }
      } catch (err: any) {
        console.error('Error loading dashboard data:', err);
        setError('Error al cargar los datos del dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
    // eslint-disable-next-line
  }, []);

  const handleManageProducts = () => {
    navigate('/products');
  };

  const handleViewBudgets = () => {
    navigate('/budgets');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Inicio {user?.role === 'admin' ? '- Administración' : '- Mi Panel'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Acciones rápidas */}
      <Typography variant="h5" gutterBottom sx={{ mt: 2, mb: 3 }}>
        Acciones Rápidas
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Nuevo Presupuesto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Crear un nuevo presupuesto para un cliente
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                size="large"
                onClick={handleCreateBudget}
                disabled={loading}
              >
                Crear Presupuesto
              </Button>
            </CardContent>
          </Card>
        </Box>

        {user?.role === 'admin' && (
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Gestionar Stock
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Agregar, editar o eliminar productos
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Inventory />}
                  fullWidth
                  size="large"
                  onClick={handleManageProducts}
                  disabled={loading}
                >
                  Gestionar Stock
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}

        {user?.role !== 'admin' && (
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ver Stock
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Consultar catálogo de productos disponibles
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Inventory />}
                  fullWidth
                  size="large"
                  onClick={handleManageProducts}
                  disabled={loading}
                >
                  Ver Stock
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}

        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {user?.role === 'admin' ? 'Ver Presupuestos' : 'Mis Presupuestos'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {user?.role === 'admin' 
                  ? 'Revisar y gestionar presupuestos existentes' 
                  : 'Revisar y gestionar mis presupuestos'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Receipt />}
                fullWidth
                size="large"
                onClick={handleViewBudgets}
                disabled={loading}
              >
                {user?.role === 'admin' ? 'Ver Presupuestos' : 'Mis Presupuestos'}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Estadísticas de productos - Stock */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Estadísticas de Stock
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {/* Más usados */}
        <Box sx={{ flex: '1 1 350px', minWidth: '350px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main">
                ⭐ Más Vendidos
              </Typography>
              <Box sx={{ height: 300, overflowY: 'auto', pr: 1 }}>
                {mostUsedProducts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No hay datos</Typography>
                ) : (
                  mostUsedProducts.map((prod, idx) => {
                    const isProfile = prod.grupo?.toLowerCase() === 'perfil';
                    const unit = isProfile ? 'u' : 'm²';
                    const quantity = prod.total_quantity ?? 0;
                    return (
                      <Box key={prod.id} sx={{ mb: 2, pb: 1, borderBottom: idx < mostUsedProducts.length - 1 ? '1px solid #eee' : 'none' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{idx + 1}. {prod.grupo} {prod.categoria} {prod.color}</Typography>
                          <Typography variant="body2" color="success.main">{quantity.toFixed(2)} {unit}</Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sin stock */}
        <Box sx={{ flex: '1 1 350px', minWidth: '350px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="flex-end" gap={1}>
                <Block color="error" sx={{ mb: '10px' }} />
                <Typography variant="h6" gutterBottom color="error.main">
                  Sin Stock
                </Typography>
              </Box>
              <Box sx={{ height: 300, overflowY: 'auto', pr: 1 }}>
                {productsWithoutStock.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No hay productos sin stock</Typography>
                ) : (
                  productsWithoutStock.map((prod, idx) => (
                    <Box key={prod.id} sx={{ mb: 2, pb: 1, borderBottom: idx < productsWithoutStock.length - 1 ? '1px solid #eee' : 'none' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{idx + 1}. {prod.grupo} {prod.categoria} {prod.color}</Typography>
                        <Typography variant="body2" color="error.main">Stock: {prod.stock}</Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Outlet */}
        <Box sx={{ flex: '1 1 350px', minWidth: '350px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="flex-end" gap={1}>
                <Hotel color="warning" sx={{ mb: '10px' }} />
                <Typography variant="h6" gutterBottom color="warning.main">
                  Outlet
                </Typography>
              </Box>
              <Box sx={{ height: 300, overflowY: 'auto', pr: 1 }}>
                {outletProducts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No hay productos outlet</Typography>
                ) : (
                  outletProducts.map((prod: ProductStatsData, idx: number) => (
                    <Box key={prod.id} sx={{ mb: 2, pb: 1, borderBottom: idx < outletProducts.length - 1 ? '1px solid #eee' : 'none' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{idx + 1}. {prod.grupo} {prod.categoria} {prod.color}</Typography>
                        <Typography variant="body2" color="warning.main">Stock: {prod.stock}</Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Resumen de estadísticas */}
      <Typography variant="h5" gutterBottom>
        {user?.role === 'admin' ? 'Estadísticas Principales' : 'Mis Estadísticas'}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Receipt sx={{ mr: 2, fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">
                    {user?.role === 'admin' ? 'Presupuestos' : 'Mis Presupuestos'}
                  </Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : stats.budgets_this_month}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.role === 'admin' ? 'Creados este mes' : 'Creados este mes'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Inventory sx={{ mr: 2, fontSize: 40, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h6">Stock</Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : stats.total_active_products}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Productos activos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AttachMoney sx={{ mr: 2, fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">Ventas Mensuales</Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : `U$ ${(stats.total_sold_revenue_usd || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.role === 'admin' ? 'Total este mes' : 'Mis ventas este mes'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {user?.role === 'admin' && (
          <>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Receipt sx={{ mr: 2, fontSize: 40, color: 'info.main' }} />
                    <Box>
                      <Typography variant="h6">Total Presupuestos</Typography>
                      <Typography variant="h4">
                        {loading ? <CircularProgress size={24} /> : stats.total_budgets}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Histórico completo
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <AttachMoney sx={{ mr: 2, fontSize: 40, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h6">Ventas Totales</Typography>
                      <Typography variant="h4">
                        {loading ? <CircularProgress size={24} /> : `U$ ${(stats.total_sold_revenue_usd || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total histórico
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </>
        )}

        {user?.role !== 'admin' && (
          <>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Receipt sx={{ mr: 2, fontSize: 40, color: 'info.main' }} />
                    <Box>
                      <Typography variant="h6">Total Mis Presupuestos</Typography>
                      <Typography variant="h4">
                        {loading ? <CircularProgress size={24} /> : stats.total_budgets}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Histórico completo
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <AttachMoney sx={{ mr: 2, fontSize: 40, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h6">Mis Ventas Totales</Typography>
                      <Typography variant="h4">
                        {loading ? <CircularProgress size={24} /> : `U$ ${(stats.total_sold_revenue_usd || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total histórico
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Box>

      {/* Métricas de Rendimiento */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        {user?.role === 'admin' ? 'Métricas de Rendimiento' : 'Mis Métricas'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box sx={{ mr: 2 }}>
                  <Typography variant="h3" color="success.main">%</Typography>
                </Box>
                <Box>
                  <Typography variant="h6">Tasa de Conversión</Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : `${stats.conversion_rate_to_sold}%`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    USD - Presupuestos vendidos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AttachMoney sx={{ mr: 2, fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">Valor Promedio</Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : `U$ ${stats.average_budget_value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por presupuesto
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Distribución por estados */}
      <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
        Estado de Presupuestos
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary">Borradores</Typography>
            <Typography variant="h4" color="grey.600">
              {loading ? <CircularProgress size={20} /> : stats.budgets_by_status.draft}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Typography variant="h6" color="info.main">Cerrados</Typography>
            <Typography variant="h4" color="info.main">
              {loading ? <CircularProgress size={20} /> : stats.budgets_by_status.closed}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Typography variant="h6" color="success.main">Vendidos</Typography>
            <Typography variant="h4" color="success.main">
              {loading ? <CircularProgress size={20} /> : stats.budgets_by_status.sold}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Estadísticas de usuarios - Solo para admin */}
      {user?.role === 'admin' && userActivityStats && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            Rendimiento de Usuarios
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            {/* Usuarios más activos */}
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="flex-end" gap={1}>
                    <People color="success" sx={{ mb: '10px' }} />
                    <Typography variant="h6" gutterBottom color="success.main">
                      Usuarios Más Activos
                    </Typography>
                  </Box>
                  {userActivityStats.most_active_users.slice(0, 3).map((user, index) => (
                    <Box key={user.user_id} sx={{ mb: 2, pb: 1, borderBottom: index < 2 ? '1px solid #eee' : 'none' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {index + 1}. {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="h6" color="success.main">
                            {user.total_budgets}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            presupuestos
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>

            {/* Mejores tasas de conversión */}
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="flex-end" gap={1}>
                    <EmojiEvents color="success" sx={{ mb: '10px' }} />
                    <Typography variant="h6" gutterBottom color="success.main">
                      Mejor Conversión
                    </Typography>
                  </Box>
                  {userActivityStats.best_conversion_users.slice(0, 3).map((user, index) => (
                    <Box key={user.user_id} sx={{ mb: 2, pb: 1, borderBottom: index < 2 ? '1px solid #eee' : 'none' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {index + 1}. {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.closed_budgets}/{user.total_budgets} cerrados
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="h6" color="success.main">
                            {user.conversion_rate}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            conversión
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </>
      )}

      {/* Estadísticas de ventas por usuario - Solo para admin */}
      {user?.role === 'admin' && userSalesStats && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            Top Vendedores
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            {/* Top por monto */}
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="flex-end" gap={1}>
                    <AttachMoney color="success" sx={{ mb: '10px' }} />
                    <Typography variant="h6" gutterBottom color="success.main">
                      Top por Ventas ($)
                    </Typography>
                  </Box>
                  {userSalesStats.top_sellers_by_amount.slice(0, 3).map((user, index) => (
                    <Box key={user.user_id} sx={{ mb: 2, pb: 1, borderBottom: index < 2 ? '1px solid #eee' : 'none' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {index + 1}. {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.contribution_percentage}% del total
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="h6" color="success.main">
                            U$ {user.total_sales_amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.total_closed_budgets} ventas
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>

            {/* Resumen de usuarios */}
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="flex-end" gap={1}>
                    <BarChart color="info" sx={{ mb: '10px' }} />
                    <Typography variant="h6" gutterBottom color="info.main">
                      Resumen de Usuarios
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Total de usuarios</Typography>
                    <Typography variant="h5">{userActivityStats?.total_users || 0}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Usuarios activos</Typography>
                    <Typography variant="h5" color="success.main">{userActivityStats?.active_users || 0}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Usuarios inactivos</Typography>
                    <Typography variant="h5" color="warning.main">{userActivityStats?.inactive_users || 0}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </>
      )}

    </Container>
  );
};
